// Copyright 2026, Imprivata, Inc.  All rights reserved.

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_MINUTE = 60 * 1000;

/**
 * Parses an ISO-8601 calendar-day string (`YYYY-MM-DD`) into a Date anchored at
 * UTC midnight on that day. The BFF ships `mfaRequiredDate` as a
 * `java.time.LocalDate`; server-side enforcement runs in UTC, so the UI
 * anchors the deadline to UTC as well to stay consistent with what the BFF
 * gates on. Returns `null` for unparseable or out-of-range inputs.
 */
const parseUtcCalendarDay = (value: string): Date | null => {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) {
    return null;
  }
  const [, ys, ms, ds] = match;
  const y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  const date = new Date(Date.UTC(y, m - 1, d));
  // Reject inputs that JS Date silently rolls over (e.g. 2026-13-01, 2026-02-30).
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) {
    return null;
  }
  return date;
};

export type MfaEnrollmentStatus = {
  deadline: Date;
  expiresAt: Date;
  daysRemaining: number;
  hoursRemaining: number;
  minutesRemaining: number;
  isPastDeadline: boolean;
};

export const getMfaEnrollmentStatus = (
  mfaRequiredDate?: string | null,
  mfaEnrolled?: boolean | null,
): MfaEnrollmentStatus | null => {
  if (mfaEnrolled) {
    return null;
  }
  if (!mfaRequiredDate) {
    return null;
  }
  // mfaRequiredDate IS the deadline. The banner shows from login until the
  // deadline passes, regardless of how far away the deadline is. Access is
  // valid through the end of the deadline day in UTC; the actual expiry
  // instant is the start of the next UTC day, which is also what backend
  // enforcement gates on.
  const deadline = parseUtcCalendarDay(mfaRequiredDate);
  if (deadline === null) {
    return null;
  }
  const expiresAt = new Date(deadline.getTime() + MS_PER_DAY);
  const now = new Date();
  const msUntilExpiry = expiresAt.getTime() - now.getTime();
  const isPastDeadline = msUntilExpiry <= 0;

  if (isPastDeadline) {
    return { deadline, expiresAt, daysRemaining: 0, hoursRemaining: 0, minutesRemaining: 0, isPastDeadline };
  }

  const startOfTodayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const daysRemaining = Math.floor((deadline.getTime() - startOfTodayUtc) / MS_PER_DAY);
  // Floor so hoursRemaining represents whole hours of access still guaranteed.
  // 0 is the sentinel the banner uses to switch to the minute-based countdown.
  const hoursRemaining = Math.floor(msUntilExpiry / MS_PER_HOUR);
  // Floor so minutesRemaining represents whole minutes still guaranteed.
  // Only meaningful when hoursRemaining === 0 (sub-hour band).
  const minutesRemaining = Math.floor(msUntilExpiry / MS_PER_MINUTE);

  return { deadline, expiresAt, daysRemaining, hoursRemaining, minutesRemaining, isPastDeadline };
};
